import { el, btn, backButton, trophySvg } from '@/ui/dom';
import { cssColor, fmtRaceTime } from '@/ui/format';
import { citySilhouette } from '@/ui/citySilhouettes';
import { CITIES } from '@/config/cities';
import type { DifficultyLevel } from '@/config/tuning';
import type { SaveData } from '@/core/SaveData';
import { isCityUnlocked } from '@/core/SaveData';
import { BUY_GAME_LABEL } from '@/ui/lockCta';
import { promoPanel, type PromoPanel } from '@/ui/promoPanel';
import { cityTrophy } from '@/core/Progression';
import type { City } from '@/config/cities';

/**
 * CitySelectScreen.ts — the Quick Race grid: one card per city (skyline
 * silhouette tinted with the city's own accent, difficulty dots, your best
 * trophy + time). Locked cities unlock by finishing them in the Tour.
 * The SKILL chip row is a view of the persisted difficulty setting (it mirrors
 * Settings' DIFFICULTY chips: one saved level for every offline race, owner
 * rule 2026-07-15). Field size is FIXED at you + 6 CPUs since M87 — no chips.
 */
/** M147 — the World Tour Cup showcase a non-owner sees (owner: "they should
 *  see the set of cities to tour with the paywall CTA instead of the garage").
 *  Every city card is on display; any tap, and the panel's button, buy. */
export interface TourPromoOptions {
  price: string;
  onBuy: () => void;
  /** Signed out only: the small ALREADY OWN IT? LOG IN link. */
  onLogIn?: () => void;
}

export interface CitySelectCallbacks {
  onPick: (cityIndex: number) => void;
  /** Home-modernize round: a non-owner tapped a FULL GAME city — the paywall. */
  onBuy?: () => void;
  /** The player changed the offline difficulty (persist it immediately). */
  onDifficulty: (level: DifficultyLevel) => void;
  onBack: () => void;
}

const DIFFICULTY_DOTS: Record<DifficultyLevel, number> = {
  bigkid: 1, // M83: cities never RATE bigkid (it's a player setting) — entry for the Record's shape
  easy: 1,
  medium: 2,
  hard: 3,
  legendary: 4,
};

/** Tiny glyphs for the chip rows (reference art: every chip carries an icon). */
const IC = {
  star:
    '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
    '<path d="M12 2.8l2.6 5.6 6.1.7-4.5 4.2 1.2 6-5.4-3-5.4 3 1.2-6L3.3 9.1l6.1-.7L12 2.8Z"/></svg>',
  wheel:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">' +
    '<circle cx="12" cy="12" r="8.5"/><circle cx="12" cy="12" r="2.4" fill="currentColor" stroke="none"/>' +
    '<path d="M12 3.5v6M4.7 16.3l5.2-2.9M19.3 16.3l-5.2-2.9"/></svg>',
  flag:
    '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
    '<path d="M6 2.5c.6 0 1 .4 1 1V4h11.4c.5 0 .8.6.5 1l-2.6 3.5 2.6 3.5c.3.4 0 1-.5 1H7v7.5a1 1 0 1 1-2 0v-17c0-.6.4-1 1-1Z"/></svg>',
  gauge:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">' +
    '<path d="M4 17.5a8.5 8.5 0 1 1 16 0"/><path d="M12 17.5 16.2 10"/>' +
    '<circle cx="12" cy="17.5" r="1.9" fill="currentColor" stroke="none"/></svg>',
  gear:
    '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
    '<path d="M19.4 13c0-.3.1-.7.1-1s0-.7-.1-1l2.1-1.6c.2-.2.2-.4.1-.6l-2-3.4c-.1-.2-.4-.3-.6-.2l-2.5 1a7 7 0 0 0-1.7-1l-.4-2.7c0-.2-.2-.4-.5-.4h-4c-.2 0-.4.2-.5.4L9 5.2a7 7 0 0 0-1.7 1l-2.5-1c-.2-.1-.5 0-.6.2l-2 3.4c-.1.2-.1.5.1.6L4.5 11c0 .3-.1.7-.1 1s0 .7.1 1l-2.1 1.6c-.2.2-.2.4-.1.6l2 3.4c.1.2.4.3.6.2l2.5-1a7 7 0 0 0 1.7 1l.4 2.7c0 .2.2.4.5.4h4c.2 0 .4-.2.5-.4l.4-2.7a7 7 0 0 0 1.7-1l2.5 1c.2.1.5 0 .6-.2l2-3.4c.1-.2.1-.5-.1-.6L19.4 13ZM12 15.5a3.5 3.5 0 1 1 0-7 3.5 3.5 0 0 1 0 7Z"/></svg>',
  trophy:
    '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
    '<path d="M7 3h10v2h3v3c0 2.4-1.8 4.3-4.1 4.4A5 5 0 0 1 13 15v2h3v3H8v-3h3v-2a5 5 0 0 1-2.9-2.6C5.8 12.3 4 10.4 4 8V5h3V3Zm-1 4v1c0 1.1.7 2.1 1.7 2.5A8 8 0 0 1 7 7H6Zm12 0h-1a8 8 0 0 1-.7 3.5c1-.4 1.7-1.4 1.7-2.5V7Z"/></svg>',
  car:
    '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
    '<path d="M4.2 12.5 5.6 8a2 2 0 0 1 1.9-1.4h9a2 2 0 0 1 1.9 1.4l1.4 4.5c.7.3 1.2 1 1.2 1.9v3.7a1 1 0 0 1-1 1h-1.2a1 1 0 0 1-1-1v-.9H5.2v.9a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-3.7c0-.9.5-1.6 1.2-1.9Zm2.7-4.1-1 3.3h12.2l-1-3.3a.6.6 0 0 0-.6-.4h-9a.6.6 0 0 0-.6.4ZM6 16.2a1.4 1.4 0 1 0 0-2.8 1.4 1.4 0 0 0 0 2.8Zm12 0a1.4 1.4 0 1 0 0-2.8 1.4 1.4 0 0 0 0 2.8Z"/></svg>',
} as const;

/** The skill ladder wears its own colours (green → gold) — red is reserved
 *  for primary actions since the owner's "too much red" note (M55). */
const CHIP_LEVELS: { label: string; icon: string; accent: string; level: DifficultyLevel }[] = [
  { label: 'BIG KID', icon: IC.star, accent: '#49c0ff', level: 'bigkid' },
  { label: 'EASY', icon: IC.flag, accent: '#22c47e', level: 'easy' },
  { label: 'MED', icon: IC.gauge, accent: '#f0a028', level: 'medium' },
  { label: 'HARD', icon: IC.gear, accent: '#ff6242', level: 'hard' },
  { label: 'LGND', icon: IC.trophy, accent: '#e6b422', level: 'legendary' },
];
/** A chip with an icon + label (btn() only does plain text). */
function iconChip(
  icon: string,
  label: string,
  accent: string,
  parent: HTMLElement,
  onClick: () => void,
) {
  const b = btn('', 'chip', parent, onClick);
  b.style.setProperty('--chip-accent', accent);
  b.innerHTML = `<span class="chip-ic">${icon}</span><span class="chip-label">${label}</span>`;
  return b;
}

export class CitySelectScreen {
  private root: HTMLDivElement;
  private grid: HTMLDivElement;
  private chips: HTMLButtonElement[] = [];
  private kickerEl: HTMLSpanElement;
  private titleEl: HTMLDivElement;
  private subEl: HTMLDivElement;
  private promo: PromoPanel | null = null;

  constructor(
    parent: HTMLElement,
    private cb: CitySelectCallbacks,
  ) {
    this.root = el('div', 'screen screen-cityselect screen-cine', parent);
    this.root.style.display = 'none';

    // M114b: the ONE shared back chevron, pinned to the screen's upper-left
    // (the header is a centred max-width row, so an inline button drifted
    // inward with the viewport — measured 110px off at 1280 wide).
    backButton(this.root, () => this.cb.onBack());

    const header = el('div', 'select-header', this.root);
    const titles = el('div', 'select-titles', header);
    const kick = el('div', 'select-kicker', titles);
    el('span', 'mhead-rule', kick);
    this.kickerEl = el('span', 'select-kicker-text', kick);
    this.titleEl = el('div', 'select-title', titles);
    this.subEl = el('div', 'select-sub', titles);

    const chipRow = el('div', 'select-chips', this.root);
    const chipLabel = el('span', 'select-chips-label', chipRow);
    chipLabel.textContent = 'SKILL';
    for (const c of CHIP_LEVELS) {
      const chip = iconChip(c.icon, c.label, c.accent, chipRow, () => {
        this.setDifficulty(c.level);
        this.cb.onDifficulty(c.level);
      });
      this.chips.push(chip);
    }

    this.grid = el('div', 'select-grid', this.root);
  }

  /** Highlight the chip for the persisted setting (the setting IS the state —
   *  this row is just a mirror of Settings' DIFFICULTY chips). */
  private setDifficulty(level: DifficultyLevel): void {
    this.chips.forEach((chip, i) => chip.classList.toggle('on', CHIP_LEVELS[i].level === level));
  }

  /** `storeLocked` (home-modernize round): the player is a resolved
   *  NON-OWNER — only the free teaser city (the first, New York) is open, and
   *  every other card wears a gold FULL GAME lock whose tap opens the
   *  paywall instead of shaking. */
  show(save: SaveData, storeLocked = false): void {
    this.setMode('quick');
    this.setDifficulty(save.settings.difficulty); // mirror the persisted setting
    this.grid.replaceChildren();

    CITIES.forEach((city, idx) => {
      const forSale = storeLocked && idx !== 0;
      const unlocked = !forSale && isCityUnlocked(save, city.id);
      const card = this.buildCard(
        city,
        `city-card${unlocked ? '' : ' locked'}${forSale ? ' for-sale' : ''}`,
      );

      const meta = el('div', 'city-card-meta', card);
      const dots = el('div', 'city-card-dots', meta);
      dots.title = `Rivals: ${city.difficulty}`;
      for (let d = 0; d < 4; d++) {
        const dot = el('span', 'dot', dots);
        if (d < DIFFICULTY_DOTS[city.difficulty]) dot.classList.add('on');
      }
      const best = el('div', 'city-card-best', meta);
      const trophy = cityTrophy(save, city.id);
      const rec = save.cities[city.id];
      if (trophy !== 'none') best.innerHTML = trophySvg(trophy);
      if (rec?.bestTimeMs != null) {
        const t = el('span', 'city-card-time', best);
        t.textContent = fmtRaceTime(rec.bestTimeMs);
      }

      if (!unlocked) {
        const lock = el('div', 'city-card-lock', card);
        lock.innerHTML =
          '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 10V8a5 5 0 0 1 10 0v2h1a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h1Zm2 0h6V8a3 3 0 0 0-6 0v2Z"/></svg>';
        const hint = el('div', 'city-card-lockhint', card);
        hint.textContent = forSale ? BUY_GAME_LABEL : 'Tour to unlock';
      }

      card.addEventListener('click', () => {
        if (forSale && this.cb.onBuy) {
          this.cb.onBuy();
          return;
        }
        if (!unlocked) {
          card.classList.remove('shake');
          void card.offsetWidth; // restart the shake animation
          card.classList.add('shake');
          return;
        }
        this.cb.onPick(idx);
      });
    });

    this.root.style.display = '';
  }

  /** M147 — the World Tour Cup showcase for a resolved non-owner: all nine
   *  cities on display (photo, name, country — no locks, records or skill
   *  chips) beside the promo panel. Tapping a city buys, like the panel. */
  showTourPromo(opts: TourPromoOptions): void {
    this.setMode('tour');
    this.grid.replaceChildren();
    for (const city of CITIES) {
      const card = this.buildCard(city, 'city-card city-card-showcase');
      card.setAttribute('aria-label', `${city.name}, ${city.country}. ${BUY_GAME_LABEL}`);
      card.addEventListener('click', () => opts.onBuy());
    }
    this.promo = promoPanel(this.root, {
      kicker: 'WORLD TOUR CUP',
      headline: 'RACE THE WORLD FOR THE CUP',
      price: opts.price,
      onBuy: opts.onBuy,
      onLogIn: opts.onLogIn,
      className: 'tour-promopanel',
    });
    this.root.style.display = '';
    this.root.scrollTop = 0;
  }

  /** Header copy + layout class per face; drops any showcase promo panel. */
  private setMode(mode: 'quick' | 'tour'): void {
    this.promo?.root.remove();
    this.promo = null;
    this.root.classList.toggle('city-tourpromo', mode === 'tour');
    if (mode === 'tour') {
      this.kickerEl.textContent = `${CITIES.length} CITIES · 1 CHAMPION`;
      this.titleEl.innerHTML =
        '<span class="mhead-chrome">WORLD</span> <span class="mhead-gold">TOUR</span>';
      this.subEl.textContent = 'Race every city for the cup. They all come with the game.';
    } else {
      this.kickerEl.textContent = 'SINGLE RACE';
      this.titleEl.innerHTML =
        '<span class="mhead-chrome">QUICK</span> <span class="mhead-gold">RACE</span>';
      this.subEl.textContent = 'Pick a city — finish cities in the Tour to unlock more';
    }
  }

  /** One city card's shell: the photo art (M82: a REAL photo of the city,
   *  public/cities/<id>.webp — licensed Wikimedia shots, credits in
   *  Settings; the owner's art can replace any file — over the procedural
   *  night-sky skyline that stays underneath as the fallback if an image is
   *  missing), the name and the country. */
  private buildCard(city: City, className: string): HTMLButtonElement {
    const card = el('button', className, this.grid);
    card.type = 'button';
    card.style.setProperty('--card-accent', cssColor(city.theme.roadLine));
    const art = el('div', 'city-card-art', card);
    art.innerHTML = `<span class="city-card-sil">${citySilhouette(city.id)}</span>`;
    const photo = document.createElement('img');
    photo.className = 'city-card-photo';
    photo.alt = '';
    photo.addEventListener('error', () => photo.remove());
    photo.src = `${import.meta.env.BASE_URL}cities/${city.id}.webp`;
    art.appendChild(photo);
    el('div', 'city-card-name', card).textContent = city.name;
    el('div', 'city-card-country', card).textContent = city.country;
    return card;
  }

  hide(): void {
    this.root.style.display = 'none';
  }

  dispose(): void {
    this.root.remove();
  }
}
